import { property } from 'lit/decorators.js';
import { hideLoadingCurtain, getVersion } from '../modules/global_function';
import { KryptonConstants } from '../modules/krypton_constants';
import { isIE11, localizeScript, scripter, unscripter } from '../modules/scripter';
import { styler } from '../modules/styler';
import { KryptonEventDetail } from './krypton-event-detail';
import { LayerElement } from './layer-element';
import { ContextMenu } from './menu-context';

export class PageElement extends LayerElement {

    _pin = false;
    onContextMenu: any;
    onExecuteMenu: any;
    isPageCreatedFromAFormSubmit: boolean = false;
    onRefreshDocument: any;
    scrollPosition: number = 0;
    @property({ type: LayerElement, reflect: true, converter: {} }) accessor opener;
    @property({ type: HTMLElement, reflect: true, converter: {} }) accessor manager; //-- avoiding circular dependency
    @property({ type: String }) accessor action: string;
    @property({ type: String, reflect: true }) accessor access: string;
    @property({ type: String, reflect: true }) accessor page: string;
    @property({ type: Array }) accessor scriptElements: HTMLScriptElement[] = [];
    @property({ type: Array }) accessor styleElements: HTMLStyleElement[] = [];
    @property({ type: Array }) accessor subpages: PageElement[] = [];
    @property({ type: ContextMenu, reflect: true, converter: {} }) accessor contextMenu: ContextMenu | null = null;
    @property({ type: Array }) accessor disconnectHandlers: Function[] = [];
    @property({ type: Array }) accessor nextPageHandlers: Function[] = [];

    _moduleTitle;
    isPublicPage: boolean | undefined = false;
    get moduleTitle() {
        if (!this._moduleTitle) {
            let m = this.querySelector("module-header") as any;
            this._moduleTitle = m?.moduleTitle;
        }
        return this._moduleTitle;
    }

    @property({ type: String, reflect: true })
    set moduleTitle(moduleTitle) {
        const oldValue = this._moduleTitle;
        this._moduleTitle = moduleTitle;
        this.requestUpdate("moduleTitle", oldValue);
    }

    _module;
    get module() {
        if (!this._module) {
            let m = this.querySelector("module-header") as any;
            this._module = m?.module;
        }
        return this._module;
    }

    @property({ type: String, reflect: true })
    set module(module) {
        const oldValue = this._module;
        this._module = module;
        this.requestUpdate("module", oldValue);
    }

    constructor(action: string, content?: string, opener?: PageElement, publicPage?: boolean) {
        super();
        this.isPublicPage = publicPage;
        if (action) {
            this.action = action;
            const tmp = action.replace(KryptonConstants.servletNameRegex, "")
            let access = tmp.match(/(^View|Update|Show)/i);
            this.access = access ? access[0] : "unknown";
            this.page = tmp.replace(/.*\./, "");
        }

        this.initializeContent(content);
        this.opener = opener || this.parentElement || null;
        if (opener) {
            opener.subpages.push(this);
        }
        this.addEventListener('click', (e: Event) => {
            if (this.kryptonApp() && this.kryptonApp().nav.open) this.kryptonApp().closeNav();
        });
    }

    initializeContent(content?: string) {
        this.kryptonApp() && this.kryptonApp().setLoadingPage(this);
        if (content) {
            const fragment = this.getContentHtml(content);
            this.initializeScripts(fragment);
            this.initializeStyles(fragment);
            this.initializeForms(fragment);
            for (let c of [...fragment.childNodes]) {
                this.appendChild(c);
            }
        }
    }

    reinitializePageToContent(content?: string) {
        this.scrollPosition = 0;
        this._moduleTitle = false;
        this._module = false;
        let frm = this.querySelector("form");
        if (frm) {
            frm = frm.openerForm;
        }
        this.textContent = "";
        this.scriptElements = [];
        this.styleElements = [];
        this.initializeContent(content);
        this.applyScriptsAndStyles();
        let newFrm = this.querySelector("form");
        if (frm && newFrm) {
            newFrm.openerForm = frm;
        }
    }

    async loadJavaScript(url: any) {
        let versionedUrl = url + "?v=" + getVersion();
        if (this.isPublicPage) {
            versionedUrl = url + "?v=" + this["publicAppVersion"];
            let alreadyLoaded = document.querySelectorAll("script[src='" + versionedUrl + "']")[0];
            if (alreadyLoaded) {
                console.log("Not loading script", versionedUrl);
                return new Promise((resolve, reject) => { resolve(true); });
            }
        }
        let promise = new Promise((resolve, reject) => {
            window["jQuery"].ajax({
                url: versionedUrl,
                dataType: 'script',
                cache: true,
                success: () => { resolve(true); },
                error: () => { reject('Ajax request failed'); }
            });
        });
        return promise;
    }

    async applyScriptsAndStyles() {
        // Let's apply styles before scripts to avoid flickering
        this.styleElements.forEach(s => {
            this.appendChild(s);
        });
        let promiseArray = [] as Promise<any>[];
        for (const s of this.scriptElements) {
            if (s.hasAttribute("src")) {
                if (s.getAttribute("src")) {
                    promiseArray.push(this.loadJavaScript(s.getAttribute("src")));
                    this.appendChild(s);
                }
            } else {
                await Promise.all(promiseArray);
                promiseArray = [];
                var e = document.createElement("script");
                var inlineScript = document.createTextNode(s.text);
                e.appendChild(inlineScript);
                this.appendChild(e);
            }
        }
        //wait for any remaining scripts
        await Promise.all(promiseArray);

        if (this.onContextMenu) {
            // There could be ledgers with no data.  In such cases, export and print has to be disabled, so let's invoke onContextMenu() before user sees it
            this.onContextMenu();
        }
        setTimeout(() => this.finalizeScriptsOnThePage(), KryptonConstants.layerOpenAnimationTime);
    }

    async animateHide() { // Let's make page-elements hide as instant to avoid flickering
        super.animateHide(true);
    }

    finalizeScriptsOnThePage() {
        if (this.open) {
            this.kryptonApp() && this.kryptonApp().processShowPageHandler();
        }
        if (!this.querySelector("message-ticker")) {
            hideLoadingCurtain();
        }
    }

    initializeScripts(source: HTMLElement) {
        // By default set onRefreshDocument to openModule to handle scenarios in which page doesn't define onRefreshDocument.  If page defines it,
        // this will be overridden when the scripts are applied
        this.onRefreshDocument = (action: any) => { window["submitEmptyForm"](this, action) };
        for (let s of source.querySelectorAll("script")) {

            let script: HTMLScriptElement | void;

            if (s.hasAttribute("noscript")) {
                s.parentElement?.removeChild(s);
                continue;
            }

            if (isIE11 && s.getAttribute("type") == "module") { //TODO import statements will not work
                if (script = localizeScript(s)) {
                    this.scriptElements.push(script);
                    s.parentElement?.removeChild(s);
                    continue;
                }
            }
            if (s.hasAttribute("src")) {
                this.scriptElements.push(s);
                s.parentElement?.removeChild(s);
                continue;
            }
            if (script = scripter(s)) {
                this.scriptElements.push(script);
                s.parentElement?.removeChild(s);
                continue;
            }
        }
    }

    initializeStyles(source: HTMLElement) {
        const scopedId = source.querySelector("main")?.getAttribute("id") as string;
        for (let s of source.querySelectorAll("style")) {
            s.parentElement?.removeChild(s);
            let css: HTMLStyleElement | void;
            css = styler(s, scopedId);
            if (css) {
                this.styleElements.push(css);
            } else {
                this.styleElements.push(s);
            }
        }
    }

    initializeForms(source: HTMLElement) {
        if (this.isPublicPage) return;
        for (let f of source.querySelectorAll("form")) {
            this.decorateForm(f, this);
        }
    }

    registerDisconnectHandler(func: Function) {
        this.disconnectHandlers.push(func);
    }

    decorateForm(form: any, page: PageElement) {

        form.setAttribute("ready", "true");
        form.setAttribute("target", KryptonConstants.defaultFormTarget);

        // Event handler to disable default form submit by buttons within the form.  It will be changed to submitForm
        form.addEventListener("submit", async (e) => {
            e.preventDefault();
            await window["submitForm"](form);
        });
        // Just an event handler will not stop form submits when javascript code invokes form.submit().  
        // The way to stop it is to overwrite form's submit method with our own
        form.forceSubmit = form.submit; // Keep a copy before it is overridden
        form.submit = (async (e: Event) => {
            if (e) {
                e.preventDefault();
            }
            await window["submitForm"](form);
            return false;
        });
    }

    scrollIfNeeded() {
        const scrollPosition = this.scrollPosition;
        setTimeout(() => {
            window.scroll(0, scrollPosition);
        }, 200);
    }

    connectedCallback() {
        this.fire(KryptonEventDetail.PAGE_ELEMENT_CONNECTED, new KryptonEventDetail(KryptonEventDetail.CONNECTED, KryptonEventDetail.PAGE_ELEMENT_CONNECTED, {}));
        super.connectedCallback();
    }

    disconnectedCallback() {
        if (this.manager) {
            this.manager.removePage(this);
            this.manager.nextPageHandlers = this.manager.nextPageHandlers.concat(this.nextPageHandlers);
        }
        //-- orphan subpages
        this.subpages.forEach(p => p.opener = null);
        //-- remove orphans this.subpages.forEach(p => p.parentElement?.removeChild(p));
        if (KryptonConstants.optimizeMemory) {
            this.scriptElements.forEach(script => {
                script.parentElement?.removeChild(script);
                unscripter(script);
            });
        }
        this.doCleanup();
        super.disconnectedCallback();
    }

    doCleanup() {
        this.disconnectHandlers.forEach(func => {
            try {
                func();
            } catch (e) {
                console.error(e);
            }
        });
        try {
            this.querySelectorAll('.tooltip').forEach(tooltip => tooltip.remove());
        } catch (e) {
            console.error(e);
        }
    }

    onDisconnect(disconnectHandler: Function) {
        if (!disconnectHandler.toString().startsWith('function')) {
            console.warn("arrow functions will not have thier context bound to this page-element");
        }
        const context = this;
        const contextualHandler = function () { disconnectHandler.apply(context) };
        this.disconnectHandlers.push(contextualHandler);
    }

    onNextPage(nextPageHandler: Function) {
        if (!nextPageHandler.toString().startsWith('function')) {
            console.warn("arrow functions will not have thier context bound to this page-element");
        }
        const context = this;
        const contextualHandler = function (nextPage) { nextPageHandler.apply(context, [nextPage]) };
        this.nextPageHandlers.push(contextualHandler);
    }

    // Overridden from  LayerElement - triggers whenver show/hide element is completed
    handleEvent(eventType: any) {
        let eventName: string = KryptonEventDetail.SHOW_PAGE_COMPLETED_EVENT;
        if (eventType === KryptonEventDetail.HIDE) {
            eventName = KryptonEventDetail.HIDE_PAGE_COMPLETED_EVENT;
        }
        this.fire(eventName, new KryptonEventDetail(eventType, eventName, { localName: this.localName, target: this }));
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'page-element': PageElement;
    }
}
